export const SETUP_TRAINING_ENCOUNTER_TYPE = 'setup_training';

export const FIRST_SETUP_MINUTES_ERROR =
  'Initial Setup/Training encounters in this RPM session must have 0 session minutes. Log additional care time in a separate encounter.';

const STATUS_PRIORITY = { active: 3, onHold: 2, preService: 1 };

/**
 * Mirrors pickCurrentProgramSession in functions/utils/programSession.js.
 * @param {Array<object>|null|undefined} programs
 * @returns {{ program: object, session: object }|null}
 */
export function pickCurrentRpmProgramSession(programs) {
  let bestProgram = null;
  let bestSession = null;
  let bestScore = -1;
  for (const prog of programs || []) {
    if (String(prog?.serviceTypeAbbreviation || '').toUpperCase() !== 'RPM') continue;
    const sessions = Array.isArray(prog.sessions) ? prog.sessions : [];
    if (sessions.length === 0) continue;
    const statusScore = STATUS_PRIORITY[prog.status] || 0;
    const score = 10 + statusScore;
    if (score > bestScore) {
      bestScore = score;
      bestProgram = prog;
      bestSession = sessions[sessions.length - 1];
    }
  }
  if (!bestProgram || !bestSession) return null;
  return { program: bestProgram, session: bestSession };
}

/**
 * @param {Array<object>|null|undefined} programs
 * @returns {{ sessionNumber: number, enrollmentDate: string, dischargeDate: string|null, setupExternallyBilled: boolean }|null}
 */
export function resolveRpmSessionContextFromPrograms(programs) {
  const picked = pickCurrentRpmProgramSession(programs);
  if (!picked) return null;
  const { session } = picked;
  const enrollmentDate = session.enrollmentDate || null;
  if (!enrollmentDate || typeof enrollmentDate !== 'string') return null;
  const sessionNumber = session.sessionNumber || (Array.isArray(picked.program.sessions) ? picked.program.sessions.length : 1);
  const dischargeDate =
    session.dischargeDate && typeof session.dischargeDate === 'string' ? session.dischargeDate : null;
  return {
    sessionNumber,
    enrollmentDate,
    dischargeDate,
    setupExternallyBilled: Boolean(session.setupExternallyBilled),
  };
}

/**
 * @param {string|null|undefined} effectiveDate YYYY-MM-DD
 * @param {{ enrollmentDate: string, dischargeDate?: string|null }} sessionWindow
 */
export function isEffectiveDateInSessionWindow(effectiveDate, sessionWindow) {
  if (!effectiveDate || !sessionWindow?.enrollmentDate) return false;
  if (effectiveDate < sessionWindow.enrollmentDate) return false;
  if (sessionWindow.dischargeDate && effectiveDate > sessionWindow.dischargeDate) return false;
  return true;
}

/**
 * Earliest-wins: candidate is "first setup" when inside the session window and either
 * no other active setup exists or candidate date is <= earliest existing setup date.
 * Sessions whose setup was billed in a prior system ("setup already billed" at
 * enroll/re-enroll) are exempt: the real initial setup happened outside the app,
 * so no in-app encounter is ever "the initial setup" for that session.
 */
export function isFirstSetupTrainingEncounter({ effectiveDate, sessionWindow, earliestExisting }) {
  if (!sessionWindow || sessionWindow.setupExternallyBilled) return false;
  if (!isEffectiveDateInSessionWindow(effectiveDate, sessionWindow)) return false;
  if (!earliestExisting) return true;
  return effectiveDate <= earliestExisting.effectiveDate;
}

/**
 * @returns {{ maxMinutes: number, isFirstSetup: boolean, sessionNumber: number|null }}
 */
export function resolveSetupTrainingMinutesCap({
  encounterType,
  effectiveDate,
  sessionWindow,
  earliestExisting,
  sessionNumber = null,
}) {
  if (encounterType !== SETUP_TRAINING_ENCOUNTER_TYPE || !sessionWindow) {
    return { maxMinutes: 180, isFirstSetup: false, sessionNumber: null };
  }
  const isFirstSetup = isFirstSetupTrainingEncounter({ effectiveDate, sessionWindow, earliestExisting });
  return {
    maxMinutes: isFirstSetup ? 0 : 180,
    isFirstSetup,
    sessionNumber: isFirstSetup ? sessionNumber : null,
  };
}
